/*
 * Copyright 2024 NewMedia Centre - Delft University of Technology
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client'

// import dependencies
import { useEffect, useState } from 'react'
import { useWebApp } from '@/hooks/webapp'
import { useRouter } from 'next/navigation'
import { Scope } from '@prisma/client'

// import server actions
import { getPermissionsAction, addPermissionAction, updatePermissionAction, updateWriteScopeAction, deletePermissionAction } from '@/actions/webapps/permissions'

// import components
import Icon from '@mdi/react'
import { Input, Select } from '@/components/input'
import { Button } from '@/components/buttons'
import {
	Field,
	Fieldset,
	Label,
	Legend
} from '@headlessui/react'

// import icons
import {
	mdiCheckboxBlankOutline,
	mdiCheckboxIntermediate,
	mdiEmailArrowRightOutline,
	mdiTrashCan
} from '@mdi/js'

// import types
import type { User, WebAppPermission } from '@prisma/client'

export const PermissionSettings = () => {
	const webApp = useWebApp()
	const router = useRouter()

	// create state
	const [permissions, setPermissions] = useState<(WebAppPermission & { user: User })[]>([])
	const [scope, setScope] = useState(webApp.writeData)
	const [email, setEmail] = useState('')

	// get permissions
	useEffect(() => {
		const get = async () => {
			const [permissions] = await getPermissionsAction({ id: webApp.id })
			permissions && setPermissions(permissions)
		}

		get()
	}, [webApp, setPermissions])

	// add permissions
	const add = async () => {
		await addPermissionAction({ id: webApp.id, email })
		router.refresh()
	}

	// set permissions
	const update = async (userId: string, permission: Partial<WebAppPermission>) => {
		await updatePermissionAction({
			id: webApp.id,
			userId,
			...permission
		})
		router.refresh()
	}

	// change write data scope
	const updateWriteScope = async () => {
		await updateWriteScopeAction({ id: webApp.id, scope })
		router.refresh()
	}

	// remove permissions
	const remove = async (userId: string) => {
		await deletePermissionAction({
			id: webApp.id,
			userId
		})
		router.refresh()
	}

	return (
		<Fieldset className='flex flex-col gap-3 p-3'>
			<div className='flex flex-col'>
				<Legend className='text-xl font-semibold'>User Permissions</Legend>
				<span className='text-xs font-light'>Defines who has permission to do what on this web app.</span>
			</div>
			<Field as='form' className='flex items-stretch gap-5' action={updateWriteScope}>
				<div className='flex w-64 flex-col'>
					<Label className='font-semibold'>Write User Data</Label>
					<span className='text-xs font-light'>Defines the policy of who is allowed to send user data to this web app.</span>
				</div>
				<div className='flex grow items-start gap-3'>
					<Select value={scope} collection={[Scope.PUBLIC, Scope.EDUCATION, Scope.INTERNAL, Scope.PRIVATE]} onChange={x => x !== null && setScope(x)} />
					<Button type='submit'>Update</Button>
				</div>
			</Field>
			<div className='flex flex-col items-stretch gap-5'>
				<Label className='font-semibold'>Manage Permissions</Label>
				<table className='table w-full table-fixed border-separate border-spacing-0 rounded border border-800 bg-black'>
					<thead className='text-xs'>
						<tr>
							<th align='left' className='px-2 py-1 font-semibold'>Name</th>
							<th className='w-24 px-2 py-1 font-semibold'>Edit</th>
							<th className='w-24 px-2 py-1 font-semibold'>Read Data</th>
							{webApp.writeData === Scope.PRIVATE && (
								<th className='w-24 px-2 py-1 font-semibold'>Write Data</th>
							)}
							<th className='w-24 px-2 py-1 font-semibold'>Manage Data</th>
							<th className='w-24 px-2 py-1 font-semibold'>Manage</th>
							<th className='w-12 px-2 py-1 font-semibold'></th>
							<th className='w-12 px-2 py-1 font-semibold'></th>
						</tr>
					</thead>
					<tbody>
						{permissions.map(permission => (
							<tr key={permission.userId}>
								<td className='border-t border-800 px-2 py-1'>{permission.user.name}</td>
								<td align='center' className='cursor-pointer border-t border-800 px-2 py-1 hover:bg-900' onClick={() => update(permission.userId, { editApp: !permission.editApp })}>
									<Icon path={permission.editApp ? mdiCheckboxIntermediate : mdiCheckboxBlankOutline} size={1} />
								</td>
								<td align='center' className='cursor-pointer border-t border-800 px-2 py-1 hover:bg-900' onClick={() => update(permission.userId, { readData: !permission.readData })}>
									<Icon path={permission.readData ? mdiCheckboxIntermediate : mdiCheckboxBlankOutline} size={1} />
								</td>
								{webApp.writeData === Scope.PRIVATE && (
									<td align='center' className='cursor-pointer border-t border-800 px-2 py-1 hover:bg-900' onClick={() => update(permission.userId, { writeData: !permission.writeData })}>
										<Icon path={permission.writeData ? mdiCheckboxIntermediate : mdiCheckboxBlankOutline} size={1} />
									</td>
								)}
								<td align='center' className='cursor-pointer border-t border-800 px-2 py-1 hover:bg-900' onClick={() => update(permission.userId, { manageData: !permission.manageData })}>
									<Icon path={permission.manageData ? mdiCheckboxIntermediate : mdiCheckboxBlankOutline} size={1} />
								</td>
								<td align='center' className='cursor-pointer border-t border-800 px-2 py-1 hover:bg-900' onClick={() => update(permission.userId, { manageApp: !permission.manageApp })}>
									<Icon path={permission.manageApp ? mdiCheckboxIntermediate : mdiCheckboxBlankOutline} size={1} />
								</td>
								<td align='center' className='cursor-pointer border-t border-800 px-2 py-1 hover:bg-900'>
									<a href={`mailto:${permission.user.email}`}>
										<Icon path={mdiEmailArrowRightOutline} size={1} />
									</a>
								</td>
								<td align='center' className='cursor-pointer border-t border-800 px-2 py-1 text-red-500 hover:bg-900' onClick={() => remove(permission.userId)}>
									<Icon path={mdiTrashCan} size={1} />
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<Field as='form' className='flex items-stretch gap-5' action={add}>
				<div className='flex w-64 flex-col'>
					<Label className='font-semibold'>Add User</Label>
					<span className='text-xs font-light'>Enter the e-mail address of the user to add permissions.</span>
				</div>
				<div className='flex grow items-start gap-3'>
					<Input required placeholder='E-Mail...' value={email} onChange={e => setEmail(e.target.value)} />
					<Button type='submit'>Add User</Button>
				</div>
			</Field>
		</Fieldset>
	)
}